import { CheckCircle2 } from "lucide-react";
import { InterestPhoneForm } from "../components/InterestPhoneForm";
import { Button } from "../components/ui/button";
import { activities, contact } from "../data/siteData";
import { useSEO } from "../hooks/useSEO";

export default function Activities() {
  const startBooking = (activityTitle) => {
    const bookingRoute = {
      "Surf Lessons": {
        whatsapp: contact.whatsappIntents.bookSession,
        phone: contact.managerPhoneRaw,
      },
      "Kite Surfing": {
        whatsapp: contact.whatsappIntents.kiteLesson,
        phone: contact.ownerPhoneRaw,
      },
    }[activityTitle];

    if (!bookingRoute) return;

    const useWhatsapp = window.confirm("Press OK for WhatsApp, or Cancel for a phone call.");
    window.location.href = useWhatsapp ? bookingRoute.whatsapp : `tel:${bookingRoute.phone}`;
  };

  useSEO({
    title: "Activities — Surf, Kite & Beach Sessions",
    description: "Beginner-friendly surf lessons, kite surfing, kayaking interest list, SUP rentals coming soon, and community beach events in Kannur, Kerala.",
    path: "/activities",
  });

  return (
    <main data-testid="activities-page" className="bg-[#0A0F1C] px-5 pb-24 pt-32 text-white md:px-10 md:pb-32">
      <section data-testid="activities-hero-section" className="mx-auto max-w-7xl">
        <p data-testid="activities-hero-kicker" className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF5E3A]">Activities</p>
        <div className="mt-6 grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-end">
          <h1 data-testid="activities-hero-title" className="font-heading text-5xl font-black leading-none tracking-tighter sm:text-6xl">
            Soft waves, safe coaching, good ocean days.
          </h1>
          <p data-testid="activities-hero-description" className="max-w-xl text-base leading-8 text-slate-400 md:text-lg">
            Kannur is a mellow place to begin. Lessons are friendly, safety-focused, and guided by instructors who keep the ocean calm, clear, and fun.
          </p>
        </div>
      </section>

      <section data-testid="activities-list-section" className="mx-auto mt-16 grid max-w-7xl gap-8">
        {activities.map((activity, index) => (
          <article
            data-testid={`activity-card-${activity.title.toLowerCase().replaceAll(" ", "-")}`}
            key={activity.title}
            className={`grid overflow-hidden rounded-[2rem] border border-white/10 bg-[#1C1917]/50 backdrop-blur-xl transition-all duration-300 hover:-translate-y-2 hover:border-white/20 hover:bg-[#1C1917]/70 lg:grid-cols-12 ${index % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""}`}
          >
            <div className="lg:col-span-5">
              <img data-testid={`activity-image-${index}`} src={activity.image} alt={`${activity.title} at Kannur Surf Club`} loading="lazy" decoding="async" className="aspect-[4/3] h-full w-full object-cover object-center" />
            </div>
            <div className="p-7 lg:col-span-7 lg:p-10">
              <p data-testid={`activity-kicker-${index}`} className="text-xs font-bold uppercase tracking-[0.28em] text-[#FFC059]">{activity.kicker}</p>
              <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <h2 data-testid={`activity-title-${index}`} className="font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">{activity.title}</h2>
                <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.16em] text-slate-300">
                  {activity.comingSoon && <span data-testid={`activity-coming-soon-${index}`} className="rounded-full border border-[#FFC059]/40 bg-[#FFC059]/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-[#FFC059]">Coming Soon</span>}
                </div>
              </div>
              <p data-testid={`activity-description-${index}`} className="mt-5 max-w-2xl text-base leading-8 text-slate-400">{activity.description}</p>
              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                {activity.details.map((detail) => (
                  <div data-testid={`activity-detail-${index}-${detail.toLowerCase().replaceAll(" ", "-")}`} key={detail} className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-slate-300">
                    <CheckCircle2 className="h-4 w-4 text-[#0D9488]" />
                    {detail}
                  </div>
                ))}
              </div>
              {(activity.title === "Surf Lessons" || activity.title === "Kite Surfing") && (
                <Button
                  data-testid={`activity-book-now-button-${index}`}
                  type="button"
                  onClick={() => startBooking(activity.title)}
                  className="mt-6 rounded-xl bg-[#FF5E3A] px-8 font-semibold uppercase tracking-[0.16em] text-white hover:bg-[#FF7B5C]"
                >
                  Book Now
                </Button>
              )}
              {activity.comingSoon && (
                <div data-testid={`activity-interest-panel-${index}`} className="mt-8 p-0">
                  <p data-testid={`activity-interest-copy-${index}`} className="text-sm leading-7 text-slate-300">Want this when it launches? Leave your phone number so the club can estimate early demand.</p>
                  <InterestPhoneForm interest={activity.title} source="activities-page" testId={`activity-interest-${index}`} />
                </div>
              )}
            </div>
          </article>
        ))}
      </section>

      <section data-testid="activities-cta-section" className="mx-auto mt-16 max-w-7xl rounded-[2rem] border border-white/10 bg-[#080C16]/80 p-8 backdrop-blur-xl md:p-12">
        <div className="grid gap-8 md:grid-cols-[1fr_auto] md:items-center">
          <div>
            <p data-testid="activities-cta-kicker" className="text-xs font-bold uppercase tracking-[0.28em] text-[#FF5E3A]">Need help choosing?</p>
            <h2 data-testid="activities-cta-title" className="mt-4 font-heading text-3xl font-bold text-white sm:text-4xl">We’ll match the session to your dates, group size, and comfort level.</h2>
          </div>
          <Button data-testid="activities-cta-contact-button" asChild className="h-12 rounded-xl bg-[#FF5E3A] px-8 font-semibold uppercase tracking-[0.16em] text-white hover:bg-[#FF7B5C]">
            <a href={contact.whatsappIntents.activities} target="_blank" rel="noreferrer">Ask the Club</a>
          </Button>
        </div>
      </section>
    </main>
  );
}
